import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import type { DNSZone } from '../../../types/dns';

interface BulkDeleteModalProps {
  isOpen: boolean;
  selectedCount: number;
  zone: DNSZone;
  confirmText: string;
  setConfirmText: (text: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({
  isOpen,
  selectedCount,
  zone,
  confirmText,
  setConfirmText,
  onConfirm,
  onCancel
}) => {
  const confirmInputRef = useRef<HTMLInputElement | null>(null);

  const canDelete = confirmText && 
    (confirmText === zone.name || confirmText.toUpperCase() === 'DELETE');

  // Focus input and handle keyboard events
  useEffect(() => {
    if (isOpen) {
      const timeout = setTimeout(() => {
        confirmInputRef.current?.focus();
      }, 0);

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onCancel();
        }
        if (e.key === 'Enter' && canDelete) {
          e.preventDefault();
          onConfirm();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        clearTimeout(timeout);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, canDelete, onConfirm, onCancel]);

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md mx-4"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-heading-2 text-slate-900 dark:text-white">
              Confirm Bulk Delete
            </h3>
            <p className="text-body-small text-slate-500 dark:text-slate-400">
              This action cannot be undone
            </p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-body text-slate-700 dark:text-slate-300 mb-4">
            You are about to delete <strong>{selectedCount}</strong> DNS records from{' '}
            <strong>{zone.name}</strong>.
          </p>
          <p className="text-body-small text-slate-600 dark:text-slate-400 mb-3">
            Type <strong>{zone.name}</strong> or <strong>DELETE</strong> to confirm:
          </p>
          <input
            ref={confirmInputRef}
            type="text"
            className="input w-full"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type "${zone.name}" or "DELETE"`}
          />
        </div>

        <div className="flex items-center justify-end space-x-3">
          <motion.button
            className="inline-flex items-center justify-center px-4 py-2 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onCancel}
          >
            Cancel
          </motion.button>
          <motion.button
            className={`inline-flex items-center justify-center px-4 py-2 text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
            whileHover={canDelete ? { scale: 1.02 } : {}}
            whileTap={canDelete ? { scale: 0.98 } : {}}
            onClick={onConfirm}
            disabled={!canDelete}
          >
            Delete {selectedCount} Records
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkDeleteModal;